'use client'

import { type AllHTMLAttributes, Fragment, useCallback, useContext, useRef, useState } from 'react'

import cn from 'clsx'
import { Button } from '@/components/Button/Button'
import { ThemeContext } from '@/utils/themeContext'
import { SearchBase, ArrowRight, X } from '@doist/icons/brand'

import {
    BaseField,
    type BaseFieldLabelVariant,
    type BaseFieldProps,
} from '../base-field/base-field'

import css from './search-field.module.css'

type InputProps = AllHTMLAttributes<HTMLInputElement>

export type SearchFieldProps = Omit<BaseFieldProps, 'action' | 'icon' | 'required' | 'variant'> &
    BaseFieldLabelVariant & {
        value: NonNullable<InputProps['value']>
        onBlur?: InputProps['onBlur']
        onFocus?: InputProps['onFocus']
        onChange?: InputProps['onChange']
        onSearch?: (query: string) => void
        clearLabel?: string
        searchLabel?: string
        placeholder?: InputProps['placeholder']
        initialSubmittedValue?: boolean
        submittedOnChanged?: boolean
    }

export function SearchField({
    id,
    value = '',
    onBlur,
    onFocus,
    onChange,
    onSearch,
    clearLabel = 'Clear',
    searchLabel = 'Search',
    placeholder,
    initialSubmittedValue = false,
    submittedOnChanged = false,
    ...restProps
}: SearchFieldProps) {
    const inputRef = useRef<HTMLInputElement | null>(null)
    const { theme } = useContext(ThemeContext)
    const [submitted, setSubmitted] = useState(initialSubmittedValue)
    const [privateValue, setPrivateValue] = useState(value)

    const searchHandler = useCallback(() => {
        if (privateValue) {
            onSearch?.(privateValue.toString())
            setSubmitted(true)
        }
    }, [onSearch, privateValue])

    const clearHandler = useCallback(() => {
        onSearch?.('')
        setSubmitted(false)
        setPrivateValue('')
        inputRef.current?.focus()
    }, [onSearch])

    const changeHandler: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        onChange?.(e)
        setPrivateValue(e.target.value)
        setSubmitted(submittedOnChanged ? e.target.value.length > 0 : false)
    }

    const keyUpHandler: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
        if (e.key === 'Enter') {
            searchHandler()
        }
    }

    return (
        <BaseField
            {...restProps}
            id={id}
            value={privateValue}
            textSize={theme === 'twist' ? 'body-sm' : 'body-base'}
            icon={<SearchBase />}
            action={
                !submitted ? (
                    <Button
                        onClick={searchHandler}
                        startIcon={ArrowRight}
                        variant={theme === 'twist' ? 'quaternary' : 'secondary'}
                        aria-label={searchLabel}
                        disabled={!privateValue || restProps.disabled}
                    />
                ) : (
                    <Button
                        onClick={clearHandler}
                        startIcon={X}
                        variant={theme === 'twist' ? 'quaternary' : 'secondary'}
                        aria-label={clearLabel}
                        disabled={restProps.disabled}
                    />
                )
            }
        >
            {({ className, ...fieldProps }, icon, action) => {
                return (
                    <Fragment>
                        {icon}
                        <input
                            type="search"
                            value={privateValue}
                            onChange={changeHandler}
                            onKeyUp={keyUpHandler}
                            onFocus={onFocus}
                            onBlur={onBlur}
                            placeholder={placeholder}
                            className={cn([className, css.input])}
                            {...fieldProps}
                            ref={inputRef}
                        />
                        {action}
                    </Fragment>
                )
            }}
        </BaseField>
    )
}
